import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { createJobs, getDigest, type Digest } from "../api";
import { PersonEntryTable } from "../components/PersonEntryTable";
import { PageHelpHandle } from "../components/PageHelpHandle";
import { toast } from "../components/Toaster";
import { havocTier, HAVOC_SCORE_EXPLAINER } from "../lib/commentary";
import type { PersonInput } from "../types";

// ─────────────────────────────────────────────────────────────────────────
// /lookup - focused entry form for queuing new people.
//
// The greeting + slideshow + cohort stats moved to /today (the new home
// page). What's left here is THE FORM and a small stats sidebar - the
// page is now purely about "add a new person to the docket."
//
// Visual language pulled from Damon's existing DDC report aesthetic:
// massive Fraunces serif, solid colored cards, eyebrow caps, generous
// whitespace, color used to differentiate not to decorate.
// ─────────────────────────────────────────────────────────────────────────

export function LookupPage() {
  const [submitting, setSubmitting] = useState(false);
  const [digest, setDigest] = useState<Digest | null>(null);

  useEffect(() => {
    // We still load the digest for StatsBoard. Greeting goes unused
    // here now (it's the centerpiece of /today instead).
    getDigest().then(setDigest).catch(console.error);
  }, []);

  const handleSubmit = async (people: PersonInput[]) => {
    setSubmitting(true);
    try {
      const created = await createJobs(people);
      const p = created[0];
      if (p) {
        toast(`Queued ${p.first_name} ${p.last_name}`, "success");
      }
    } catch (err) {
      toast(err instanceof Error ? err.message : String(err), "danger");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page-shell">
      {/* Compact intake band. Client-bearing copy rule: no jokes, no
          aphorisms, no presumption of guilt or re-offense. */}
      <div className="page-card page-card-flush" data-tour="lookup-form">
        <div
          className="page-hero lookup-compact-hero"
          style={{ position: "relative", minHeight: "auto" }}
        >
          <div style={{ position: "absolute", top: 16, right: 18 }}>
            <PageHelpHandle
              query="What is the Lookup page for, and how should I add someone to the docket?"
              topicId="search-input-cheat-sheet"
            />
          </div>
          <div className="page-hero-eyebrow">Add to docket</div>
          <h1 className="page-hero-title">Add one person.</h1>
          <p className="page-hero-caption">
            Queue the capture, then keep moving to the next intake.
          </p>
        </div>
        <div style={{ padding: "14px clamp(16px, 3vw, 28px) 22px" }}>
          <PersonEntryTable onSubmit={handleSubmit} submitting={submitting} />
        </div>
      </div>

      <div className="page-card">
        <StatsBoard digest={digest} />
      </div>

      <LookupFooterLinks />
    </div>
  );
}

// Tiny footer links - kept deliberately small + low-contrast so they
// don't compete with the lookup form. These are operator tools (not user
// features), most useful when reviewing whether the search variants are
// earning their CAPTCHAs or when checking source-status priorities.
function LookupFooterLinks(): JSX.Element {
  return (
    <footer
      className="container lookup-footer-links"
      style={{
        marginTop: 40,
        marginBottom: 0,
        display: "flex",
        gap: 18,
        flexWrap: "wrap",
        fontFamily: "var(--font-mono)",
        // Bump tiny size + opacity so the operator-only links stay
        // findable without dominating the page. 0.42 was unreadable
        // on low-brightness displays.
        fontSize: 12,
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        opacity: 0.7,
      }}
    >
      <Link
        to="/search-variants"
        style={{ color: "var(--color-text-dim)", textDecoration: "none", borderBottom: "1px dotted currentColor", paddingBottom: 1 }}
      >
        Search variants, receipts
      </Link>
      <Link
        to="/admin/scrape-health"
        style={{ color: "var(--color-text-dim)", textDecoration: "none", borderBottom: "1px dotted currentColor", paddingBottom: 1 }}
      >
        Source status, priorities
      </Link>
    </footer>
  );
}

// (GreetingHero + GreetingText moved to /today; the latter lives at
//  src/client/components/GreetingText.tsx for shared reuse.)

// ─────────────────────────────────────────────────────────────────────────
// Stats board - solid-color cards inspired by the DDC essential-stats grid
// ─────────────────────────────────────────────────────────────────────────

function StatsBoard({ digest }: { digest: Digest | null }) {
  if (!digest) {
    return (
      <section className="container station">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="skeleton h-32 w-full rounded-2xl" />
          ))}
        </div>
      </section>
    );
  }

  const { stats } = digest;

  return (
    <section className="container station">
      <div className="mb-6">
        <div className="eyebrow text-[color:var(--color-text-muted)]">Docket status</div>
        <h2
          className="display mt-2 text-[color:var(--color-text)]"
          style={{
            fontSize: "clamp(24px, 2.6vw, 32px)",
            letterSpacing: "-0.01em",
            fontVariationSettings: '"opsz" 60, "SOFT" 50',
          }}
        >
          Current capture activity.
        </h2>
      </div>

      {/* HERO STAT ROW - all solid deep colors, white text. Each one a
          different rich hue so the row reads as a sequence (not noise). */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <BigStatCard
          label="People captured"
          value={stats.totalPeople.toLocaleString()}
          accent="rose"
          sub={
            stats.totalCompleted > 0
              ? `${stats.totalCompleted} fully read`
              : "fresh ledger"
          }
        />
        <BigStatCard
          label="Total records"
          value={stats.totalRecords.toLocaleString()}
          accent="ink"
          sub={
            stats.totalRecords > 0 && stats.totalPeople > 0
              ? `${(stats.totalRecords / Math.max(1, stats.totalPeople)).toFixed(1)} per person, average`
              : ""
          }
        />
        <BigStatCard
          label="Peak triage"
          value={stats.peakHavoc !== null ? stats.peakHavoc.toString() : "NA"}
          accent="crimson"
          sub={
            stats.peakHavoc !== null && havocTier(stats.peakHavoc)
              ? havocTier(stats.peakHavoc)!.label
              : "no scored records yet"
          }
        />
        <BigStatCard
          label="In the queue"
          value={stats.totalActive.toLocaleString()}
          accent="orange"
          sub={
            stats.totalActive > 0 ? "still being read" : "all caught up"
          }
        />
      </div>

      {/* TOP-3 HAVOC SHOWCASE */}
      {stats.topHavoc.length > 0 && (
        <div className="mt-8">
          <div className="eyebrow mb-3 text-[color:var(--color-text-muted)]" title={HAVOC_SCORE_EXPLAINER}>Highest current triage</div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4">
            {stats.topHavoc.map((p, i) => {
              // Rank-tier accent: full-width strip at the top of each
              // card. Encodes the rank without resorting to side-stripes
              // (banned) or color-fill cards (would crowd the colored
              // BigStatCard row above). Reads as an editorial section
              // banner: cherry for #1, bronze for #2, gold-cream for #3.
              const accent = i === 0 ? "var(--color-rose-hi)"
                : i === 1 ? "var(--color-amber-hi)"
                : "var(--color-gold-cream-hi)";
              return (
                <Link
                  key={p.id}
                  to={`/records/${p.id}`}
                  className="group relative block overflow-hidden rounded-2xl border border-[color:var(--color-line)] bg-[color:var(--color-bg-card)] p-5 transition hover:border-[color:var(--color-line-hi)] hover:-translate-y-0.5 hover:shadow-lg sm:p-6"
                >
                  <span
                    aria-hidden
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 4,
                      background: accent,
                    }}
                  />
                  <div className="flex items-baseline justify-between gap-2">
                    <span
                      className="meta"
                      style={{ color: accent, fontWeight: 700, letterSpacing: "0.2em", flexShrink: 0 }}
                    >
                      #{i + 1}
                    </span>
                    <HavocPill score={p.havoc_score} />
                  </div>
                  <div
                    className="display mt-3 text-[color:var(--color-text)] group-hover:text-[color:var(--color-rose-hi)] transition-colors"
                    style={{
                      fontSize: "clamp(20px, 2vw, 26px)",
                      lineHeight: 1.1,
                      fontVariationSettings: '"opsz" 36, "SOFT" 50',
                    }}
                  >
                    {p.first_name} {p.last_name}
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}

function BigStatCard({
  label,
  value,
  sub,
  accent,
}: {
  label: string;
  value: string;
  sub?: string;
  accent: "rose" | "crimson" | "orange" | "ink" | "sage" | "gold";
}) {
  // All variants are now solid deep colors with white text - readable, bold,
  // and the row reads as a sequence of distinct moods. We use inline styles
  // so the colors are guaranteed exact.
  const palette: Record<string, { bg: string; tint: string }> = {
    rose:    { bg: "linear-gradient(155deg, #C13455 0%, #7A1F3A 100%)", tint: "rgba(255,255,255,0.78)" },
    crimson: { bg: "linear-gradient(155deg, #7f1d1d 0%, #450a0a 100%)", tint: "rgba(255,255,255,0.72)" },
    orange:  { bg: "linear-gradient(155deg, #d77b1f 0%, #8e4708 100%)", tint: "rgba(255,255,255,0.80)" },
    ink:     { bg: "linear-gradient(155deg, #2a2519 0%, #14110a 100%)", tint: "rgba(255,255,255,0.65)" },
    sage:    { bg: "linear-gradient(155deg, #5e7a3f 0%, #4a6131 100%)", tint: "rgba(255,255,255,0.78)" },
    gold:    { bg: "linear-gradient(155deg, #c2a534 0%, #8c721f 100%)", tint: "rgba(255,255,255,0.80)" },
  };
  const p = palette[accent]!;
  return (
    <div
      className="dark-tile rounded-2xl border border-transparent p-5 sm:p-6"
      style={{ background: p.bg, color: "white" }}
    >
      <div
        className="text-[10px] font-semibold uppercase tracking-[0.22em]"
        style={{ color: p.tint }}
      >
        {label}
      </div>
      <div
        className="display mt-3 tabular text-white"
        style={{
          fontSize: "clamp(40px, 5vw, 64px)",
          lineHeight: 0.95,
          letterSpacing: "-0.03em",
          fontVariationSettings: '"opsz" 144, "SOFT" 30',
          fontWeight: 700,
        }}
      >
        {value}
      </div>
      {sub && (
        <div className="meta mt-3" style={{ color: p.tint }}>
          {sub}
        </div>
      )}
    </div>
  );
}

function HavocPill({ score }: { score: number }) {
  const t = havocTier(score);
  if (!t) return null;
  return (
    <span
      className="inline-flex flex-shrink-0 items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em]"
      style={{
        color: t.textHi,
        borderColor: `color-mix(in oklab, ${t.textHi} 50%, transparent)`,
        background: `color-mix(in oklab, ${t.textHi} 10%, transparent)`,
        whiteSpace: "nowrap",
      }}
    >
      {score} · {t.label}
    </span>
  );
}
